import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Minus, Plus, HandPlatter, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useCustomer } from "@/context/CustomerContext";
import { PaymentOptions } from "@/components/PaymentOptions";
import { CoinBox, coinMath } from "@/components/CoinBox";
import { api, errMsg, pad2, rupiah } from "@/lib/api";
import { ContactFields, MemberNotice, useContact } from "@/components/CheckoutContact";

const CartLine = ({ item }) => {
  const { setQty, setItemNote } = useCart();
  const btn = "w-8 h-8 rounded-md border border-ink/25 bg-white/60 grid place-items-center hover:bg-sage-100 active:scale-90 transition-[background-color,transform]";
  return (
    <div className="py-3.5 border-b border-sage-200 last:border-0" data-testid={`cart-item-${item.id}`}>
      <div className="flex justify-between gap-3 font-semibold text-ink">
        <span>{item.qty}× {item.name}</span>
        <span className="whitespace-nowrap">{rupiah(item.price * item.qty)}</span>
      </div>
      <div className="flex items-center gap-3 mt-2">
        <button className={btn} onClick={() => setQty(item.id, item.qty - 1)} data-testid={`cart-item-qty-decrease-${item.id}`} aria-label="Kurangi"><Minus className="w-4 h-4" /></button>
        <span className="w-5 text-center font-medium" data-testid={`cart-item-qty-${item.id}`}>{item.qty}</span>
        <button className={btn} onClick={() => setQty(item.id, item.qty + 1)} data-testid={`cart-item-qty-increase-${item.id}`} aria-label="Tambah"><Plus className="w-4 h-4" /></button>
        <input value={item.note || ""} onChange={(e) => setItemNote(item.id, e.target.value)} maxLength={100} placeholder="Catatan menu (mis. less sugar)"
          className="flex-1 min-w-0 px-2.5 py-1.5 rounded-md border border-sage-200 bg-white/60 text-xs" data-testid={`cart-item-note-${item.id}`} />
      </div>
    </div>
  );
};

export const CartPanel = () => {
  const { items, notes, setNotes, method, seat, total, useCoins, clear, rememberOrder, setPickerOpen, setSheetOpen } = useCart();
  const { customer, refresh } = useCustomer();
  const [contact, setContact] = useContact();
  const { used, pay } = coinMath(total, customer?.coins || 0, useCoins);
  const { outlet, shop } = useCart();
  const tax = Math.round((pay * shop.taxRate) / 100);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const checkout = async () => {
    if (!items.length) return toast.error("Keranjang masih kosong. Pilih menu dulu, ya.");
    if (!seat) {
      setSheetOpen(false);
      document.getElementById("menu")?.scrollIntoView({ behavior: "smooth" });
      setTimeout(() => setPickerOpen(true), 400);
      return toast.error("Tentukan nomor meja & lantai dulu.");
    }
    if (!contact.name.trim()) return toast.error("Isi nama kamu dulu, ya.");
    if (contact.whatsapp.replace(/\D/g, "").length < 9) return toast.error("Isi nomor WhatsApp yang valid, mis. 0812xxxxxxxx.");
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        items: items.map((i) => ({ menu_id: i.id, qty: i.qty, note: i.note || "" })),
        floor: seat.floor, table: seat.table, notes, payment_method: method, use_coins: customer ? used : 0, outlet,
        name: contact.name.trim(), whatsapp: contact.whatsapp, promo_consent: contact.promo_consent,
      });
      toast.dismiss();
      if (customer && !data.customer_id) toast.warning("Sesi member kamu sudah berakhir, pesanan ini tercatat sebagai tamu tanpa Coin. Masuk lagi dengan Gmail untuk pesanan berikutnya.", { duration: 6000 });
      rememberOrder(data);
      clear();
      refresh();
      setSheetOpen(false);
      navigate(`/bayar/${data.id}?t=${data.access_token}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-sage-100 border border-sage-200 rounded-2xl p-4 sm:p-5" data-testid="order-cart-panel">
      <div className="flex items-center justify-between px-1">
        <h2 id="pesananmu" className="font-serif font-semibold text-2xl text-ink">Pesananmu</h2>
        <button onClick={() => setPickerOpen(true)} className="text-sm px-3 py-1 rounded-lg border border-ink/20 bg-paper" data-testid="cart-table-badge">
          {seat ? `Meja ${pad2(seat.table)} • Lt ${seat.floor}` : "Pilih meja"}
        </button>
      </div>
      <div className="bg-paper rounded-xl px-4 mt-4">
        {items.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted2" data-testid="cart-empty">Belum ada menu dipilih.<br />Tekan tombol + pada menu favoritmu.</p>
        ) : items.map((i) => <CartLine key={i.id} item={i} />)}
        <div className="py-3 border-t border-sage-200">
          <input value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={300} placeholder="Catatan pesanan (opsional)" data-testid="cart-order-notes-input"
            className="w-full px-3 py-2.5 rounded-lg border border-ink/20 bg-white/60 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30" />
        </div>
        <ContactFields contact={contact} setContact={setContact} />
        <CoinBox />
        <div className="py-3 border-t border-sage-200 space-y-1 text-sm" data-testid="cart-breakdown">
          <div className="flex justify-between text-muted2"><span>Subtotal</span><span data-testid="cart-subtotal">{rupiah(total)}</span></div>
          <div className="flex justify-between text-muted2"><span>{shop.taxName} {shop.taxRate}%{used > 0 ? " (setelah potongan)" : ""}</span><span data-testid="cart-tax">{rupiah(tax)}</span></div>
        </div>
        <div className="flex justify-between items-center py-4 border-t border-sage-200">
          <span className="font-semibold">Total</span>
          <span className="text-2xl font-bold text-ink" data-testid="cart-total">{rupiah(pay + tax)}</span>
        </div>
      </div>
      <h3 className="font-serif font-semibold text-xl text-ink mt-6 mb-3 px-1">Metode pembayaran</h3>
      <PaymentOptions />
      <MemberNotice />
      <button onClick={checkout} disabled={busy} className="btn-forest w-full py-4 text-lg mt-5 flex items-center justify-center gap-2" data-testid="checkout-submit-button">
        {busy && <Loader2 className="w-5 h-5 animate-spin" />} Lanjut Pembayaran
      </button>
      <p className="text-xs text-center text-muted2 mt-3">Pesanan diproses setelah pembayaran dikonfirmasi. Kode unik 3 digit ditambahkan ke total saat pembayaran.</p>
      <div className="mt-4 flex items-center justify-center gap-3 rounded-xl bg-sage-200/60 py-3.5 text-sm font-semibold text-ink">
        <HandPlatter className="w-6 h-6" strokeWidth={1.5} /> Duduk santai. Kami antar ke mejamu.
      </div>
    </div>
  );
};
